// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// API
import { deleteExternalExam } from "../../api";

export const useDeleteExternalExams = (
  clinicId: string | undefined,
  patientId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (externalExamIds: Array<string>) => {
      if (!clinicId || !patientId) throw new Error();

      try {
        const token = await getToken();
        return deleteExternalExam(token, clinicId, patientId, externalExamIds);
      } catch (error) {
        throw error;
      }
    },
    // Invalidate exams query cache
    onSuccess: (_, externalExamIds) => {
      queryClient.invalidateQueries({
        queryKey: ["external-exams", clinicId, patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["opd-external-exams", clinicId, patientId],
      });
      queryClient.invalidateQueries({
        queryKey: ["vf-external-exams", clinicId, patientId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("External exam deleted successfully!", {
          count: externalExamIds.length,
        }),
      });
    },
    onError: (_, externalExamIds) => {
      setSnackbarInfo({
        variant: "error",
        message: t("External exam could not be deleted!", {
          count: externalExamIds.length,
        }),
      });
    },
  });
};
